<template>
  <modal v-model="model" max-width="440">
    <template #title>Export Review</template>
    <template #content>
      <div class="text-body-2 modal-body font-weight-light">
        <p class="mb-2">Select what you would like to export:</p>
        <option-list v-model="options" multiple>
          <option-list-item
            :value="ReviewExportOption.EXTRACTED_DATA"
          >
            Data extracted from included studies (CSV)
          </option-list-item>
          <option-list-item
            :value="ReviewExportOption.PRISMA_STATISTICS"
          >
            PRISMA Statistics (CSV)
          </option-list-item>
          <option-list-item
            :value="ReviewExportOption.FULL_TEXT_PDFS"
          >
            Included Full Text PDFs
          </option-list-item>
        </option-list>
        <template v-if="fullTextPdfsSelected">
          <v-divider class="my-6" />
          <v-row
            no-gutters
            justify="center"
            class="font-weight-medium"
          >
            Group Full Text PDFs by
          </v-row>
          <v-row no-gutters class="my-5" justify="center">
            <dropdown
              v-model="selectedField"
              :options="fields"
              text-prop="title"
              max-width="300"
            />
          </v-row>
          <v-row
            v-if="hasError"
            no-gutters
            justify="center"
            class="my-5"
          >
            <v-alert
              :value="hasError"
              type="error"
              icon="error_outline"
              class="ma-0 caption"
              borderless
              dismissible
              dense
              rounded="pill"
              @input="error = null"
            >
              {{ error }}
            </v-alert>
          </v-row>
        </template>
      </div>
    </template>
    <template #actions>
      <action-button class="mr-3" @click="model = false">
        Cancel
      </action-button>
      <action-button color="accent" @click="onExport">
        <v-progress-circular
          v-if="exporting"
          class="mx-2"
          indeterminate
          size="25"
          width="2"
        />
        <template v-else>
          <v-icon left class="material-icons-outlined">
            system_update_alt
          </v-icon>
          Export Review
        </template>
      </action-button>
    </template>
  </modal>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton';
import Modal from '@/components/Common/Modal';
import Dropdown from '@/components/Common/Dropdown';
import OptionList from '@/components/Common/OptionList';
import OptionListItem from '@/components/Common/OptionListItem';
import { ReviewExportOption } from '@/enums';
import { typeHelpers } from '@/modules';
import { DataExtractionFieldBase } from '@/models';

export default {
  name: 'ExportReviewDialog',
  components: {
    ActionButton,
    Modal,
    Dropdown,
    OptionList,
    OptionListItem,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      default: () => [],
      validator: (field) =>
        typeHelpers.isArrayOfType(field, DataExtractionFieldBase),
    },
  },
  emits: ['input', 'export'],
  data: () => ({
    exporting: false,
    selectedField: null,
    error: null,
    options: Object.values(ReviewExportOption),
  }),
  computed: {
    model: {
      get: function () {
        return this.value;
      },
      set: function (value) {
        this.$emit('input', value);
      },
    },
    hasSelectedField() {
      return !!this.selectedField;
    },
    hasError() {
      return !!this.error;
    },
    ReviewExportOption() {
      return ReviewExportOption;
    },
    fullTextPdfsSelected() {
      return this.options.includes(ReviewExportOption.FULL_TEXT_PDFS);
    },
  },
  watch: {
    model: function (model) {
      if (model) {
        this.error = null;
        this.exporting = false;
        this.selectedField = null;
      }
    },
    selectedField: function () {
      this.error = null;
    },
  },
  methods: {
    onExport() {
      if (this.exporting) return;
      if (this.fullTextPdfsSelected && !this.hasSelectedField) {
        this.error = 'Please select a field above.';
        return;
      }
      this.exporting = true;
      this.error = null;
      this.$emit('export', {
        options: this.options,
        groupByField: this.selectedField,
      });
    },
  },
};
</script>

<style scoped>
.modal-body {
  line-height: 150% !important;
}
</style>
